import DataLoader from 'dataloader';
import { useState, useEffect, useCallback, useRef, DependencyList } from 'react';

/**
 * Make a hook with the given dataloader.
 * @param loader The data Loader to use.
 */
export function makeLoader<K, V, C = K>(loader: DataLoader<K, V, C>) {
  return function useDataLoader(id: K | null | undefined, def: V = null) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(def);
    const [error, setError] = useState<Error>();
    useEffect(() => {
       // 🔒 Critical Guard
      if (id === null || id === undefined || id === ('' as any)) {
        setLoading(false);
        setData(def);
        setError(undefined);
        return;
      }
      let ignore = false;
      setLoading(true);
      setError(null);
      loader
        .load(id)
        .then(d => !ignore && setData(d))
        .catch(err => !ignore && setError(err))
        .finally(() => !ignore && setLoading(false));

      return () => {
        ignore = true;
      };
    }, [id]);
    return {
      loading,
      error,
      data,
    };
  };
}

type GeneratorOf<T extends Generator | AsyncGenerator> = T extends Generator<infer R>
  ? R
  : T extends AsyncGenerator<infer P>
  ? P
  : never;

function useTeardownCallback<T extends (...args: any[]) => Generator>(
  cleanable: T,
  deps: DependencyList = []
): (...args: Parameters<T>) => GeneratorOf<ReturnType<T>> {
  const cleanUp = useRef<ReturnType<T>>(null);

  useEffect(
    () => () => {
      if (cleanUp.current) {
        cleanUp.current.next();
        cleanUp.current = null;
      }
    },
    []
  );

  return useCallback((...args) => {
    if (cleanUp.current) {
      cleanUp.current.next();
      cleanUp.current = null;
    }
    const generator = cleanable(...args) as ReturnType<T>;
    const toReturn = generator.next().value;
    cleanUp.current = generator;
    return toReturn;
  }, deps);
}

/**
 * Make a hook with the given dataloader.
 * @param loader The data Loader to use.
 */
export function makeLoaderTigger<K, V, C = K>(loader: DataLoader<K, V, C>) {
  return function useDataLoader(id: K, def: V = null) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(def);
    const [error, setError] = useState<Error>();
    const fetchData = useTeardownCallback(
      function* () {
        let ignore = false;
        setLoading(true);
        setError(null);
        loader
          .load(id)
          .then(d => !ignore && setData(d))
          .catch(err => !ignore && setError(err))
          .finally(() => !ignore && setLoading(false));

        yield;

        ignore = true;
      },
      [id]
    );
    return {
      loading,
      error,
      data,
      trigger: fetchData,
    };
  };
}
